/**
 * I18nTranslationEditor — Floating overlay for super-admin in-app translation editing
 * Triggered by Ctrl+Shift+L
 * Loads all translation keys from /api/i18n/dict and allows inline editing + save to DB
 * Fixes:
 *   - Locale dropdown now loads all 18 locales (parses d.locales correctly)
 *   - Save payloads now include scope + lang_code (was sending locale / items)
 *   - Auto-translate to all 18 locales on save when locale = en-IN
 *   - Missing translation indicator per locale
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Globe, X, Search, Save, RefreshCw, CheckCircle, AlertCircle, Languages, Zap, BarChart2 } from 'lucide-react';
import { API_BASE, getToken as _getToken } from '../../lib/api';

function getToken() {
  return (
    sessionStorage.getItem('rcq_access_token') ||
    localStorage.getItem('rcq_access_token') ||
    sessionStorage.getItem('retailcliq_token') ||
    localStorage.getItem('retailcliq_token') ||
    ''
  );
}

function isSuperAdmin() {
  try {
    const raw = localStorage.getItem('rcq_user') || sessionStorage.getItem('rcq_user') ||
                localStorage.getItem('retailcliq_user') || sessionStorage.getItem('retailcliq_user');
    if (!raw) return false;
    const u = JSON.parse(raw);
    const role = (u?.role || '').toLowerCase();
    const name = (u?.name || u?.username || '').toLowerCase();
    // Check admin features for scoped roles (ADMINSUPPORT with DYNAMIC_ATTRIBUTES grant)
    const hasAdminFeature = (() => {
      try {
        const ctx = JSON.parse(
          localStorage.getItem('rcq_global_context') || sessionStorage.getItem('rcq_global_context') || '{}'
        );
        return (Array.isArray(ctx?.adminFeatures) ? ctx.adminFeatures : []).includes('DYNAMIC_ATTRIBUTES');
      } catch { return false; }
    })();
    return (
      role === 'super_admin' || role === 'superadmin' || role === 'sys-admin' ||
      role === 'admin' || role === 'licensor' || role === 'adminsupport' ||
      name.startsWith('ssadmin') || name.startsWith('syscliq') || name.startsWith('sysadmin') ||
      ((role === 'adminsupport' || role === 'licensor') && hasAdminFeature)
    );
  } catch { return false; }
}

export function I18nTranslationEditor() {
  const [visible, setVisible] = useState(false);
  const [entries, setEntries] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [query, setQuery] = useState('');
  const [locale, setLocale] = useState('en-IN');
  const [locales, setLocales] = useState([{ code: 'en-IN', label: 'English' }, { code: 'hi-IN', label: 'हिन्दी' }]);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [autoTranslating, setAutoTranslating] = useState(false);
  const [savedKeys, setSavedKeys] = useState(new Set());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [coverage, setCoverage] = useState({}); // locale → count
  const [totalKeys, setTotalKeys] = useState(0);
  const inputRef = useRef(null);

  // Ctrl+Shift+L toggle
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'L') {
        e.preventDefault();
        if (isSuperAdmin()) setVisible(v => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Load locales + coverage when editor opens
  useEffect(() => {
    if (!visible) return;
    // Load locales
    fetch(`${API_BASE}/api/i18n/locales`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json())
      .then(d => {
        // API returns { locales: [...] } — handle both formats
        const list = Array.isArray(d) ? d : (d.locales || []);
        if (list.length > 0) setLocales(list.map(l => ({ code: l.code || l, label: l.label || l.code || l })));
      })
      .catch(() => {});
    // Load coverage
    loadCoverage();
    loadDictionary();
  }, [visible]);

  const loadCoverage = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/i18n/admin/coverage`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (r.ok) {
        const d = await r.json();
        setCoverage(d.coverage || {});
        setTotalKeys(d.totalKeys || 0);
      }
    } catch {}
  }, []);

  // Reload when locale changes
  useEffect(() => {
    if (visible) loadDictionary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, visible]);

  const loadDictionary = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_BASE}/api/i18n/dict?locale=${locale}&format=flat-array`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const d = await r.json();
      const list = Array.isArray(d) ? d : (d.data ? d.data : Object.entries(d).map(([key, value]) => ({ key, value, locale })));
      setEntries(list);
      setFiltered(list);
      setEdits({});
      setSavedKeys(new Set());
    } catch (ex) {
      setError('Failed to load dictionary: ' + ex.message);
    } finally { setLoading(false); }
  }, [locale]);

  // Filter on query
  useEffect(() => {
    if (!query) { setFiltered(entries); return; }
    const q = query.toLowerCase();
    setFiltered(entries.filter(e => e.key?.toLowerCase().includes(q) || (e.value || '').toLowerCase().includes(q)));
  }, [query, entries]);

  const handleEdit = (key, value) => {
    setEdits(prev => ({ ...prev, [key]: value }));
  };

  const saveOne = async (key) => {
    const value = edits[key] !== undefined ? edits[key] : entries.find(e => e.key === key)?.value || '';
    const entry = entries.find(e => e.key === key);
    const scope = entry?.scope || 'app';
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/i18n/admin/upsert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ scope, key, lang_code: locale, value }),
      });
      const data = await res.json();
      setSavedKeys(prev => new Set([...prev, key]));
      setEntries(prev => prev.map(e => e.key === key ? { ...e, value } : e));
      setEdits(prev => { const n = { ...prev }; delete n[key]; return n; });
      // If saved to en-IN, show auto-translate count
      if (data.auto_translated > 0) {
        setError('');
        // Refresh coverage after auto-translate
        setTimeout(loadCoverage, 1000);
      }
    } catch (ex) { setError('Save failed: ' + ex.message); }
    setSaving(false);
  };

  const saveAll = async () => {
    const dirty = Object.keys(edits);
    if (!dirty.length) return;
    setSaving(true);
    try {
      const rows = dirty.map(key => {
        const entry = entries.find(e => e.key === key);
        return { scope: entry?.scope || 'app', key, lang_code: locale, value: edits[key] };
      });
      const res = await fetch(`${API_BASE}/api/i18n/admin/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ rows }),
      });
      const data = await res.json();
      setSavedKeys(prev => new Set([...prev, ...dirty]));
      setEntries(prev => prev.map(e => edits[e.key] !== undefined ? { ...e, value: edits[e.key] } : e));
      setEdits({});
      if (data.auto_translated > 0) {
        setTimeout(loadCoverage, 1000);
      }
    } catch (ex) { setError('Bulk save failed: ' + ex.message); }
    setSaving(false);
  };

  // Auto-translate current locale entries using backend
  const autoTranslateAll = async () => {
    if (locale !== 'en-IN') {
      setError('Auto-translate only available from en-IN source. Switch to en-IN first.');
      return;
    }
    setAutoTranslating(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/i18n/admin/auto-translate-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ force: false }),
      });
      const data = await res.json();
      if (data.success) {
        setError('');
        setTimeout(loadCoverage, 2000);
      } else {
        setError(data.error || 'Auto-translate failed');
      }
    } catch (ex) { setError('Auto-translate failed: ' + ex.message); }
    setAutoTranslating(false);
  };

  if (!visible) return null;

  const dirtyCount = Object.keys(edits).length;
  const currentCoverage = coverage[locale];
  const missingCount = totalKeys > 0 && currentCoverage !== undefined ? Math.max(0, totalKeys - currentCoverage) : null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
      <div className="bg-[#0f0f0f] border border-[#222] rounded-2xl shadow-2xl w-[820px] max-w-[95vw] h-[85vh] flex flex-col overflow-hidden"
           data-testid="i18n-translation-editor">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-[#222] shrink-0">
          <Languages size={18} className="text-blue-400" />
          <h2 className="text-sm font-black text-white uppercase tracking-wider flex-1">i18n Translation Editor</h2>
          <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest bg-[#1a1a1a] px-2 py-0.5 rounded">Ctrl+Shift+L</span>
          <button onClick={() => setVisible(false)} className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-white/10 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#1a1a1a] shrink-0 flex-wrap">
          {/* Locale picker */}
          <div className="relative">
            <select value={locale} onChange={e => setLocale(e.target.value)}
              data-testid="i18n-locale-select"
              className="text-[10px] font-bold bg-[#1a1a1a] border border-[#333] text-blue-400 rounded-lg px-2 py-1.5 outline-none cursor-pointer pr-6">
              {locales.map(l => {
                const lCode = typeof l === 'string' ? l : l.code;
                const lLabel = typeof l === 'string' ? l : (l.label || l.code);
                const cnt = coverage[lCode];
                const pct = totalKeys > 0 && cnt !== undefined ? Math.round((cnt / totalKeys) * 100) : null;
                return (
                  <option key={lCode} value={lCode}>
                    {lLabel}{pct !== null ? ` (${pct}%)` : ''}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Missing translations badge */}
          {missingCount !== null && missingCount > 0 && (
            <span className="flex items-center gap-1 text-[9px] font-bold text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-1 rounded-full">
              <AlertCircle size={10} />
              {missingCount} missing
            </span>
          )}
          {missingCount === 0 && totalKeys > 0 && (
            <span className="flex items-center gap-1 text-[9px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded-full">
              <CheckCircle size={10} />
              Complete
            </span>
          )}

          {/* Search */}
          <div className="flex-1 flex items-center gap-1.5 bg-[#1a1a1a] border border-[#333] rounded-lg px-2 py-1.5 min-w-[180px]">
            <Search size={12} className="text-slate-600 shrink-0" />
            <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Search keys or values..."
              className="flex-1 text-[10px] bg-transparent text-slate-300 placeholder:text-slate-600 outline-none" />
          </div>

          {/* Auto-translate all (en-IN only) */}
          {locale === 'en-IN' && (
            <button onClick={autoTranslateAll} disabled={autoTranslating} title="Auto-translate all en-IN keys to 17 locales"
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all border ${
                autoTranslating ? 'bg-purple-600/20 text-purple-400 border-purple-500/20 cursor-not-allowed' : 'bg-[#1a1a1a] text-purple-400 hover:text-purple-300 border-[#333] hover:border-purple-500/40'
              }`}>
              <Zap size={11} className={autoTranslating ? 'animate-pulse' : ''} />
              {autoTranslating ? 'Translating…' : 'Auto-Translate All'}
            </button>
          )}

          {/* Reload */}
          <button onClick={loadDictionary} title="Reload"
            className="p-1.5 rounded-lg bg-[#1a1a1a] text-slate-500 hover:text-white border border-[#333] transition-colors">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          </button>

          {/* Save All */}
          <button onClick={saveAll} disabled={!dirtyCount || saving} data-testid="i18n-save-all-btn"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all ${
              dirtyCount ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-[#1a1a1a] text-slate-600 cursor-not-allowed border border-[#333]'
            }`}>
            <Save size={12} />
            {dirtyCount ? `Save ${dirtyCount}` : 'No Changes'}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 mt-2 shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-[10px]">
            <AlertCircle size={12} /> {error}
            <button onClick={() => setError('')} className="ml-auto text-red-400/60 hover:text-red-400"><X size={10} /></button>
          </div>
        )}

        {/* Coverage Bar */}
        {totalKeys > 0 && (
          <div className="px-4 py-1.5 border-b border-[#1a1a1a] shrink-0">
            <div className="flex items-center gap-2">
              <BarChart2 size={10} className="text-slate-600" />
              <span className="text-[9px] text-slate-600 font-semibold">Coverage:</span>
              <div className="flex-1 flex gap-1 overflow-x-auto">
                {locales.map(l => {
                  const lCode = typeof l === 'string' ? l : l.code;
                  const cnt = coverage[lCode] || 0;
                  const pct = Math.round((cnt / totalKeys) * 100);
                  const color = pct >= 85 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444';
                  return (
                    <div key={lCode}
                      onClick={() => setLocale(lCode)}
                      title={`${lCode}: ${cnt}/${totalKeys} (${pct}%)`}
                      className={`shrink-0 text-[8px] font-bold px-1.5 py-0.5 rounded cursor-pointer transition-all ${lCode === locale ? 'ring-1 ring-blue-400' : 'opacity-60 hover:opacity-100'}`}
                      style={{ background: `${color}15`, color, border: `1px solid ${color}30` }}>
                      {lCode.split('-')[0]} {pct}%
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center gap-3 px-4 py-1.5 border-b border-[#1a1a1a] text-[9px] text-slate-600 font-semibold shrink-0">
          <span>{filtered.length} / {entries.length} keys</span>
          {dirtyCount > 0 && <span className="text-amber-400">{dirtyCount} unsaved</span>}
          {savedKeys.size > 0 && <span className="text-emerald-400">{savedKeys.size} saved this session</span>}
          {locale === 'en-IN' && <span className="text-purple-400">Auto-translate enabled on save</span>}
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-slate-600">
              <RefreshCw size={20} className="animate-spin mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-slate-600 text-xs">No translations found</div>
          ) : (
            <table className="w-full text-[10px]">
              <thead className="sticky top-0 bg-[#111]">
                <tr>
                  <th className="text-left px-4 py-2 text-[8px] font-black uppercase tracking-wider text-slate-600 w-[38%]">Key</th>
                  <th className="text-left px-4 py-2 text-[8px] font-black uppercase tracking-wider text-slate-600 w-[12%]">Scope</th>
                  <th className="text-left px-4 py-2 text-[8px] font-black uppercase tracking-wider text-slate-600">Value ({locale})</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((entry) => {
                  const val = edits[entry.key] !== undefined ? edits[entry.key] : (entry.value || '');
                  const isDirty = edits[entry.key] !== undefined;
                  const isSaved = savedKeys.has(entry.key);
                  const isEmpty = !entry.value && !isDirty;
                  return (
                    <tr key={entry.key} className={`border-b border-[#151515] hover:bg-[#1a1a1a]/40 transition-colors ${isDirty ? 'bg-amber-500/5' : ''} ${isEmpty ? 'bg-red-500/3' : ''}`}>
                      <td className="px-4 py-2 font-mono text-slate-500 select-all">
                        <span className={isEmpty ? 'text-red-500/60' : ''}>{entry.key}</span>
                      </td>
                      <td className="px-4 py-2 text-[8px] text-slate-600">{entry.scope || ''}</td>
                      <td className="px-4 py-2">
                        <input
                          value={val}
                          onChange={e => handleEdit(entry.key, e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && saveOne(entry.key)}
                          className={`w-full bg-transparent text-slate-300 outline-none focus:ring-1 focus:ring-blue-500 rounded px-1 py-0.5 transition-all ${isDirty ? 'bg-amber-500/10 text-amber-200' : ''} ${isEmpty ? 'placeholder:text-red-500/50' : ''}`}
                          placeholder="(missing translation)"
                        />
                      </td>
                      <td className="px-2 py-2 text-center">
                        {isSaved && <CheckCircle size={11} className="text-emerald-500 mx-auto" />}
                        {isDirty && !isSaved && (
                          <button onClick={() => saveOne(entry.key)} disabled={saving}
                            className="p-1 rounded text-blue-400 hover:text-blue-300 transition-colors" title="Save this key">
                            <Save size={11} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-[#222] shrink-0 flex items-center justify-between text-[9px] text-slate-600">
          <span>Super-admin only · <code className="text-slate-500">WebApp.tbl_translations</code></span>
          <span className="text-blue-500">Ctrl+Shift+L to close</span>
        </div>
      </div>
    </div>
  );
}

export default I18nTranslationEditor;
